import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { mediaUrl } from "./AdMediaGallery";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}.${String(Math.floor((s % 1) * 10))}`;

/** Scrub a video and pick the exact frame to use as its poster/thumbnail. */
export default function PosterPicker({ adId, media, onClose, onSaved }) {
  const vid = useRef();
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);
  const [busy, setBusy] = useState(false);

  const seek = (v) => { setT(v); if (vid.current) vid.current.currentTime = v; };
  const step = (d) => seek(Math.max(0, Math.min(dur || 0, t + d)));

  const save = async () => {
    const v = vid.current; if (!v || !v.videoWidth) return toast.error("Video not ready yet");
    setBusy(true);
    try {
      const scale = Math.min(1, 960 / v.videoWidth); const c = document.createElement("canvas");
      c.width = Math.round(v.videoWidth * scale); c.height = Math.round(v.videoHeight * scale);
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      const blob = await new Promise(res => c.toBlob(res, "image/jpeg", 0.85));
      if (!blob) throw new Error("Could not capture frame (video may be cross-origin)");
      const fd = new FormData(); fd.append("poster", blob, "poster.jpg");
      const { data } = await api.post(`/ads/${adId}/media/${media.id}/poster`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Thumbnail updated"); onSaved(data); onClose();
    } catch (e) { toast.error("Could not save thumbnail", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="poster-picker">
        <DialogHeader><DialogTitle className="font-heading">Choose thumbnail frame</DialogTitle></DialogHeader>
        <div className="bg-black aspect-video overflow-hidden">
          <video ref={vid} src={mediaUrl(media)} crossOrigin="anonymous" muted playsInline preload="auto" className="w-full h-full object-contain"
            onLoadedMetadata={(e) => { setDur(e.target.duration || 0); seek(Math.min(1, (e.target.duration || 2) / 2)); }} data-testid="poster-picker-video" />
        </div>
        <div className="space-y-2">
          <input data-testid="poster-picker-scrub" type="range" min={0} max={dur || 0} step={0.1} value={t} onChange={(e) => seek(Number(e.target.value))} className="w-full accent-zinc-900" />
          <div className="flex items-center justify-between text-xs text-zinc-600">
            <span className="tabular" data-testid="poster-picker-time">{fmt(t)} / {fmt(dur)}</span>
            <div className="flex gap-1">
              {[[-1, "−1s"], [-0.1, "−0.1s"], [0.1, "+0.1s"], [1, "+1s"]].map(([d, l]) => <button key={l} type="button" onClick={() => step(d)} className="h-7 px-2 border border-zinc-300 hover:border-zinc-900 text-[11px] font-semibold">{l}</button>)}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button type="button" data-testid="poster-picker-save" onClick={save} disabled={busy || !dur} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white">{busy ? "Saving…" : "Use this frame"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
